import { message } from 'antd'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { serializeMainInfoForStorage } from 'pages/reporting/reports/rug/hooks/useFormData'
import { RugFormDataState } from 'pages/reporting/reports/rug/rugReportData'
import { usePublishRevisionHandler } from 'pages/reporting/shared/hooks/usePublishRevisionHandler'
import { useSignoffHandler } from 'pages/reporting/shared/hooks/useSignoffHandler'
import { useCallback, useState } from 'react'
import { GeckoInspection, SignatureData } from 'types'

interface UseReportSubmissionProps {
  metadataEntryId: string
  formData: RugFormDataState
  updateFormData: (data: Partial<RugFormDataState>) => void
  inspections: readonly GeckoInspection[] | undefined
  currentRevisionNumber: number
  publishLatestDraftPDF: (pdfPath: string) => Promise<void>
  publishRevision: () => Promise<string>
}

interface UseReportSubmissionResult {
  isSubmitting: boolean
  handleSubmitReport: () => Promise<void>
  handleSignoff: (signatureData: SignatureData) => Promise<void>
  handlePublishRevision: (dataToUse?: RugFormDataState) => Promise<void>
}

export const useReportSubmission = ({
  metadataEntryId,
  formData,
  updateFormData,
  inspections,
  currentRevisionNumber,
  publishLatestDraftPDF,
  publishRevision,
}: UseReportSubmissionProps): UseReportSubmissionResult => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { rolodexPatch } = useRolodexEdit()

  const handleSubmitReport = useCallback(async () => {
    if (!metadataEntryId) {
      message.error('Metadata entry not yet created')
      return
    }

    try {
      setIsSubmitting(true)

      // Prepare complete report data
      const reportData = {
        main_info: serializeMainInfoForStorage(formData.mainInfo),
        section_info: {
          coverSectionData: formData.coverSectionData,
          summarySectionData: formData.summarySectionData,
          dataSheetSectionData: formData.dataSheetSectionData,
          imagesSectionData: formData.imagesSectionData,
          assetDrawings: formData.assetDrawings,
        },
      }

      // Update Rolodex entry
      await rolodexPatch([
        {
          id: metadataEntryId,
          data: reportData,
        },
      ])

      message.success('Report data submitted successfully!')
    } catch (error) {
      console.error('Failed to submit report:', error)
      message.error('Failed to submit report')
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }, [metadataEntryId, formData, rolodexPatch])

  const generatePdfPath = useCallback(
    (timestamp: string) => {
      // Create a shortened version of inspection IDs for the filename
      // If more than 3 inspections, show first 2 and count
      const inspectionIdList =
        inspections?.map((inspection) => inspection.id) || []
      let inspectionIdPart = ''

      if (inspectionIdList.length === 0) {
        inspectionIdPart = ''
      } else if (inspectionIdList.length <= 3) {
        inspectionIdPart = inspectionIdList.join('-') + '-'
      } else {
        // For many inspections, use first two IDs and count
        inspectionIdPart = `${inspectionIdList[0]}-${inspectionIdList[1]}-and${inspectionIdList.length - 2}more-`
      }

      return `reportPDF/${inspectionIdPart}rug-report-r${currentRevisionNumber}-${timestamp}.pdf`
    },
    [inspections, currentRevisionNumber]
  )

  const { handlePublishRevision } = usePublishRevisionHandler({
    metadataEntryId,
    formData,
    currentRevisionNumber,
    publishLatestDraftPDF,
    publishRevision,
    setIsSubmitting,
    generatePdfPath,
  })

  const { handleSignoff } = useSignoffHandler({
    metadataEntryId,
    formData,
    updateFormData,
    setIsSubmitting,
    handlePublishRevision,
  })

  return {
    isSubmitting,
    handleSubmitReport,
    handleSignoff,
    handlePublishRevision,
  }
}
